'use client';

import React, { useEffect, useRef, useState } from 'react';

const BRAND = '#3757FF';

const parentFeatures = [
    {
        title: 'Academic visibility',
        desc: 'See your child\'s progress across every subject — grades, attendance, and completed work — all in one view.',
        icon: (
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <circle cx="11" cy="11" r="4" stroke="currentColor" strokeWidth="1.75" />
                <path d="M2 11c2-5 5.5-7 9-7s7 2 9 7c-2 5-5.5 7-9 7s-7-2-9-7z" stroke="currentColor" strokeWidth="1.75" />
            </svg>
        ),
    },
    {
        title: 'Session activity',
        desc: 'Know which live classes your child attended, how long they engaged, and what was covered.',
        icon: (
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <rect x="2" y="4" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.75" />
                <path d="M9 9l5 2.5L9 14V9z" fill="currentColor" />
            </svg>
        ),
    },
    {
        title: 'Assessment results',
        desc: 'Review test and quiz results as soon as they\'re graded, with clear breakdowns of strengths and gaps.',
        icon: (
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <rect x="3" y="2" width="16" height="19" rx="2" stroke="currentColor" strokeWidth="1.75" />
                <path d="M7 8h8M7 12h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M13 16l1.5 1.5L17 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        title: 'Tutor communication',
        desc: 'Stay in contact with your child\'s tutors and receive updates directly through the platform.',
        icon: (
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <path d="M4 4h14a2 2 0 012 2v8a2 2 0 01-2 2H8l-4 3V6a2 2 0 012-2z" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
            </svg>
        ),
    },
];

export default function LearnForParents() {
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.1 });
        if (sectionRef?.current) observer?.observe(sectionRef?.current);
        return () => observer?.disconnect();
    }, []);

    return (
        <section id="for-parents" ref={sectionRef} className="py-28 px-6 md:px-10 relative overflow-hidden" style={{ background: '#fff' }}>
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 80% 50%, rgba(55,87,255,0.04) 0%, transparent 60%)' }} />

            <div className="max-w-7xl mx-auto relative z-10">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    {/* Left: parent dashboard mockup */}
                    <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        <div
                            className="rounded-3xl p-7 relative overflow-hidden"
                            style={{ background: 'linear-gradient(135deg, rgba(55,87,255,0.06) 0%, rgba(55,87,255,0.02) 100%)', border: '1px solid rgba(55,87,255,0.12)' }}
                        >
                            {/* Parent view header */}
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <p className="text-xs font-700 uppercase tracking-widest mb-1" style={{ color: '#999', letterSpacing: '0.12em' }}>Parent View</p>
                                    <p className="text-base font-800" style={{ color: '#0D0D0D' }}>Ama's Learning Overview</p>
                                </div>
                                <span className="text-xs font-700 px-3 py-1.5 rounded-full" style={{ background: 'rgba(55,87,255,0.1)', color: BRAND }}>This Week</span>
                            </div>

                            {/* Child summary cards */}
                            <div className="grid grid-cols-2 gap-3 mb-5">
                                {[
                                    { label: 'Sessions this week', value: '5', icon: '📅' },
                                    { label: 'Avg. score', value: '87%', icon: '📊' },
                                    { label: 'Courses active', value: '4', icon: '📚' },
                                    { label: 'Hours studied', value: '12h', icon: '⏱' },
                                ]?.map(item => (
                                    <div key={item?.label} className="rounded-2xl p-4" style={{ background: 'white', boxShadow: '0 2px 12px rgba(55,87,255,0.06)' }}>
                                        <p className="text-lg font-800 mb-0.5" style={{ color: BRAND }}>{item?.value}</p>
                                        <p className="text-xs font-600" style={{ color: '#888' }}>{item?.label}</p>
                                    </div>
                                ))}
                            </div>

                            {/* Recent activity */}
                            <div className="rounded-2xl p-5" style={{ background: 'white', boxShadow: '0 2px 12px rgba(55,87,255,0.06)' }}>
                                <p className="text-xs font-700 uppercase tracking-widest mb-4" style={{ color: '#999', letterSpacing: '0.12em' }}>Recent Activity</p>
                                <div className="flex flex-col gap-3">
                                    {[
                                        { action: 'Completed: Algebra Module 7', time: '2h ago', type: 'complete' },
                                        { action: 'Attended: Live Physics session', time: 'Yesterday', type: 'live' },
                                        { action: 'Assessment: English — 91%', time: '2 days ago', type: 'assess' },
                                    ]?.map(item => (
                                        <div key={item?.action} className="flex items-center gap-3">
                                            <div
                                                className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                                                style={{ background: 'rgba(55,87,255,0.08)' }}
                                            >
                                                <div className="w-2 h-2 rounded-full" style={{ background: BRAND }} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-600 truncate" style={{ color: '#0D0D0D' }}>{item?.action}</p>
                                                <p className="text-xs font-500" style={{ color: '#AAA' }}>{item?.time}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right: content */}
                    <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        <div
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-700 uppercase tracking-widest mb-6"
                            style={{ background: 'rgba(55,87,255,0.08)', color: BRAND, border: '1px solid rgba(55,87,255,0.15)', letterSpacing: '0.15em' }}
                        >
                            For Parents
                        </div>
                        <h2
                            className="font-display font-800 mb-5"
                            style={{ fontSize: 'clamp(1.8rem, 3.5vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.025em', color: '#0D0D0D' }}
                        >
                            Stay connected
                            <br />
                            <span style={{ color: BRAND }}>to their learning.</span>
                        </h2>
                        <p className="text-base leading-relaxed mb-10" style={{ color: '#666', maxWidth: '460px' }}>
                            Wisly Learn keeps parents informed and involved — not just at report card time, but throughout the entire learning journey.
                        </p>

                        <div className="grid grid-cols-1 gap-5">
                            {parentFeatures?.map((f) => (
                                <div key={f?.title} className="flex items-start gap-4">
                                    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(55,87,255,0.08)', color: BRAND }}>
                                        {f?.icon}
                                    </div>
                                    <div>
                                        <p className="text-sm font-700 mb-1" style={{ color: '#0D0D0D' }}>{f?.title}</p>
                                        <p className="text-sm leading-relaxed" style={{ color: '#666' }}>{f?.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
